import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import uniqid from 'uniqid';
import Case from 'case';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { DomainListTablePartial, PlatformSelectTablePartial } from '@Components/Partial/Product/CompanySettings';
import { paths } from '@Components/configs';
import {
  initiateCompanySettingsRequest,
  initiateSingleCompanySettingRequest,
  clearSingleCompanySettingRequest,
  updateCompanyDomainRequest,
} from '@Actions';


const CompanySettingsPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchCompanySettings,
  fetchCompanySetting,
  clearCompanySetting,
  updateCompanyDomain,
  paymentPending,
  companySettings = null,
  settingData = null,
}) => {
  const { id: currentSetting } = useParams();
  const [resetKey, setResetKey] = React.useState(uniqid());
  const {
    theme: { page },
    settingsMenuConfig,
    isDarkMode,
  } = useAppTheme();

  React.useEffect(() => {
    setResetKey(uniqid());
    fetchCompanySetting(currentSetting);

    if (!companySettings) fetchCompanySettings();

    return () => {
      clearCompanySetting();
    };
  }, [currentSetting]);

  const currentSettingData = _.find(companySettings, { api: currentSetting });
  const settingHint = settingsMenuConfig[currentSettingData?.api]?.hint || currentSettingData?.hint;
  const pageTitle = settingsMenuConfig[currentSettingData?.api]?.pageTitle
    || currentSettingData?.display;

  const isPlatformSetting = currentSetting.includes('platform');

  const handleDomainUpdate = (value, preValue = null, action = 'push') => {
    let requestData = {};
    const actionTerm = currentSetting.includes('author') ? 'Profile' : 'Domain';
    let successNotice = `${actionTerm} added to ${pageTitle}`;

    /* eslint-disable camelcase */
    switch (action) {
      case 'edit':
        requestData = { value, pre_value: preValue, action };
        successNotice = `${actionTerm} updated`;
        break;
      case 'delete':
        requestData = { pre_value: value, action };
        successNotice = `${actionTerm} removed from ${pageTitle}`;
        break;
      default:
        requestData = { value, action };
        break;
    }

    const body = { data: [requestData] };
    updateCompanyDomain(
      currentSetting, body, paths.settings.replace(':id', currentSetting), () => fetchCompanySetting(currentSetting), successNotice,
    );
  };

  const handlePlatformUpdate = (prevValues, newValues) => {
    const updates = [];

    /* eslint-disable camelcase */
    prevValues.forEach((value) => {
      if (!newValues.includes(value)) {
        updates.push({ pre_value: value, action: 'delete' });
      }
    });
    /* eslint-enable camelcase */

    newValues.forEach((value) => {
      if (!prevValues.includes(value)) {
        updates.push({ value, action: 'push' });
      }
    });

    const body = { data: updates };
    const successNotice = 'Platform settings updated successfully';
    updateCompanyDomain(currentSetting, body, paths.settings.replace(':id', currentSetting), () => fetchCompanySetting(currentSetting), successNotice);
  };

  let hintHeader = 'Information';

  if (currentSetting.includes('dmca')) {
    hintHeader = 'DMCA notice information';
  }

  if (currentSetting.includes('review')) {
    hintHeader = 'Review tool information';
  }

  const actionTerm = settingsMenuConfig[currentSettingData?.api]?.actionTerm || 'domain';

  const renderContent = () => {
    if (isPlatformSetting) {
      return (
        <PlatformSelectTablePartial
          key={resetKey}
          small={small}
          loading={loading || submitLoading}
          options={settingData?.values}
          selectedOptions={settingData?.value}
          updateSelections={(prevValues, newValues) => (
            handlePlatformUpdate(prevValues, newValues)
          )}
          pageTitle={pageTitle}
          paymentPending={paymentPending}
        />
      );
    }

    return (
      <DomainListTablePartial
        key={resetKey}
        small={small}
        loading={loading || submitLoading}
        domainData={settingData?.value}
        updateDomain={(value, preValue, action) => handleDomainUpdate(value, preValue, action)}
        pageTitle={pageTitle}
        actionTerm={actionTerm}
        headerMap={[
          { uid: actionTerm, display: Case.capital(actionTerm), size: small ? '75%' : '85%' },
          { uid: 'actions', display: 'Actions', size: small ? '25%' : '15%' },
        ]}
        paymentPending={paymentPending}
      />
    );
  };

  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle: 'Settings',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
      >
        {settingHint && (
          <InfoNotice
            header={hintHeader}
            text={settingHint}
          />
        )}
        {renderContent()}
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    settingData: state.companySettings.companySettingData,
    companySettings: state.companySettings.companySettings,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCompanySettings: initiateCompanySettingsRequest,
    fetchCompanySetting: initiateSingleCompanySettingRequest,
    clearCompanySetting: clearSingleCompanySettingRequest,
    updateCompanyDomain: updateCompanyDomainRequest,
  }, dispatch);
}

CompanySettingsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchCompanySettings: PropTypes.func.isRequired,
  fetchCompanySetting: PropTypes.func.isRequired,
  clearCompanySetting: PropTypes.func.isRequired,
  updateCompanyDomain: PropTypes.func.isRequired,
  companySettings: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
    hint: PropTypes.string,
  })),
  settingData: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.string.isRequired),
    values: PropTypes.arrayOf(PropTypes.string.isRequired),
  }),
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(CompanySettingsPage));
